// src/pages/Home.js
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiPenTool, FiLayers, FiTrendingUp, FiStar, FiX } from 'react-icons/fi';
import { SignedIn } from '@clerk/clerk-react';
import FadeIn from '../components/FadeIn';
import TestimonialForm from '../components/TestimonialForm';
import { useTestimonials } from '../hooks/useTestimonials';
import { useApiAuth } from '../hooks/useApiAuth';
import { useSiteConfig } from '../hooks/useSiteConfig';

function Home() {
  const { testimonials, addTestimonial, removeTestimonial } = useTestimonials();
  const { authFetch } = useApiAuth();
  const { config } = useSiteConfig();
  const carouselRef = useRef(null);
  const [activeCard, setActiveCard] = useState(0);
  const reviewsRef = useRef(null);
  const [activeReview, setActiveReview] = useState(0);

  function handleCarouselScroll() {
    const el = carouselRef.current;
    if (!el) return;
    setActiveCard(Math.round(el.scrollLeft / el.offsetWidth));
  }

  function scrollToCard(index) {
    const el = carouselRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' });
    setActiveCard(index);
  }

  function handleReviewsScroll() {
    const el = reviewsRef.current;
    if (!el) return;
    setActiveReview(Math.round(el.scrollLeft / el.offsetWidth));
  }

  function scrollToReview(index) {
    const el = reviewsRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.offsetWidth, behavior: 'smooth' });
    setActiveReview(index);
  }

  const bannerStyle = config.bannerImageUrl
    ? { backgroundImage: `url(${config.bannerImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : {};

  return (
    <>
      {/* Banner */}
      <header id="banner" className="banner" style={bannerStyle}>
        <div id="banner-content" className="banner-content">
          <h1 id="banner-title">CS Devs</h1>
          <h2 id="banner-subheading" className="banner-subheading">What We Offer</h2>
          <p id="banner-body" className="banner-body">
            We specialize in building high-performance websites using our proprietary, handcrafted
            templates. By utilizing our own custom-built foundations, we ensure your site is unique,
            lightweight, and perfectly tailored to your brand's needs.
          </p>
          <Link id="banner-cta" className="btn-cta" to="/services">Get Started</Link>
        </div>
      </header>

      {/* What We Offer */}
      <section id="offer-section" className="offer-section">
        <FadeIn>
          <h2 id="offer-title" className="offer-title">How We Help You Succeed</h2>
        </FadeIn>
        <div id="offer-cards" className="offer-cards" ref={carouselRef} onScroll={handleCarouselScroll}>
          <FadeIn delay={0}>
            <div id="offer-card-1" className="offer-card">
              <span className="offer-card-icon"><FiPenTool size={28} /></span>
              <h3 className="offer-card-heading">Core Branding</h3>
              <p className="offer-card-body">From logo and color palette to typography and visual identity, we craft a cohesive brand foundation that communicates who you are at a glance. Every visual decision is intentional — built to leave a lasting impression across every touchpoint.</p>
            </div>
          </FadeIn>
          <FadeIn delay={150}>
            <div id="offer-card-2" className="offer-card">
              <span className="offer-card-icon"><FiLayers size={28} /></span>
              <h3 className="offer-card-heading">Tailored Digital Identity</h3>
              <p className="offer-card-body">We move beyond "cookie-cutter" designs by using our own proprietary templates to build a site that truly reflects your brand. This ensures your online presence is as unique as your business, helping you stand out in a crowded market.</p>
            </div>
          </FadeIn>
          <FadeIn delay={300}>
            <div id="offer-card-3" className="offer-card">
              <span className="offer-card-icon"><FiTrendingUp size={28} /></span>
              <h3 className="offer-card-heading">Scalable Solutions</h3>
              <p className="offer-card-body">Scalable Solutions for Growth We build with your future in mind, creating flexible digital homebases that can grow alongside your ambitions. As we expand our offerings to include mobile applications, your web platform will be ready to integrate with the next level of your business.</p>
            </div>
          </FadeIn>
        </div>
        <div className="carousel-dots">
          {[0, 1, 2].map(i => (
            <button
              key={i}
              className={`carousel-dot${activeCard === i ? ' carousel-dot--active' : ''}`}
              onClick={() => scrollToCard(i)}
              aria-label={`Go to card ${i + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Parallax Divider */}
      <div id="stats-bar" className="stats-bar" />

      <section id="testimonials-section" className="testimonials-section">
        <FadeIn>
          <p className="offer-heading">What Our Clients Say</p>
          <h2 className="offer-title">Trusted by Teams Everywhere</h2>
        </FadeIn>
        <div className="testimonials-grid" ref={reviewsRef} onScroll={handleReviewsScroll}>
          {testimonials.map((t, i) => (
            <FadeIn key={t.id} delay={Math.min(i, 2) * 150}>
              <div className="testimonial-card">
                <SignedIn><button className="testimonial-delete" onClick={() => removeTestimonial(t.id, authFetch)} aria-label="Delete testimonial"><FiX size={14} /></button></SignedIn>
                <div className="testimonial-stars">
                  {[...Array(5)].map((_, s) => (
                    <FiStar key={s} size={14} className={s < t.rating ? 'star-filled' : 'star-empty'} />
                  ))}
                </div>
                <p className="testimonial-body">"{t.body}"</p>
                <div className="testimonial-author">
                  <span className="testimonial-name">{t.name}</span>
                  <span className="testimonial-role">{t.role}</span>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        {testimonials.length > 1 && (
          <div className="carousel-dots">
            {testimonials.map((_, i) => (
              <button
                key={i}
                className={`carousel-dot${activeReview === i ? ' carousel-dot--active' : ''}`}
                onClick={() => scrollToReview(i)}
                aria-label={`Go to review ${i + 1}`}
              />
            ))}
          </div>
        )}
        <FadeIn>
          <TestimonialForm onSubmit={addTestimonial} />
        </FadeIn>
      </section>
    </>
  );
}

export default Home;
